import { action, observable } from 'mobx';
import { HttpClient } from 'Mri.Web.Common';

export interface SeqEvent {
    id: string;
    timestamp: string;
    level: string;
    renderedMessage: string;
    messageTemplate: string;
    exception: string | null;
    properties: Record<string, any>;
}

/**
 * How the last Seq read went - rendered at the top of the page. Carries no key material: whether a key was
 * sent and its length, which distinguishes two configured keys without exposing either.
 */
export interface SeqQueryStatus {
    seqServer: string | null;
    apiKeyConfigured: boolean;
    apiKeyUsed: boolean;
    apiKeyLength: number;
    succeeded: boolean;
    httpStatusCode: number | null;
    message: string | null;
}

export interface SeqEventsResult {
    events: SeqEvent[];
    status: SeqQueryStatus;
}

export class SeqLogsStore {
    public constructor(httpClient: HttpClient) {
        this.httpClient = httpClient;
    }

    @observable
    public events: SeqEvent[] = [];

    @observable
    public status: SeqQueryStatus | null = null;

    @observable
    public loading = false;

    @observable
    public filter = '';

    @observable
    public level = 'All';

    @observable
    public timeRange = '1h';

    @observable
    public autoRefresh = false;

    @action
    public setFilter(filter: string) {
        this.filter = filter;
    }

    @action
    public setLevel(level: string) {
        this.level = level;
    }

    @action
    public setTimeRange(timeRange: string) {
        this.timeRange = timeRange;
    }

    @action
    public toggleAutoRefresh() {
        this.autoRefresh = !this.autoRefresh;
    }

    @action
    public async loadEventsAsync(afterId?: string) {
        this.loading = true;
        try {
            const params = new URLSearchParams();
            params.set('count', '50');

            if (this.filter) params.set('filter', this.filter);
            if (this.level && this.level !== 'All') params.set('level', this.level);
            if (afterId) params.set('afterId', afterId);

            const fromUtc = this.getFromUtc();
            if (fromUtc) params.set('from', fromUtc);

            const result = await this.httpClient.get<SeqEventsResult>(`/api/v1/seq/events?${params.toString()}`);
            if (result) {
                this.status = result.status ? result.status : null;
                if (afterId) {
                    this.events = [...this.events, ...result.events];
                } else {
                    this.events = result.events;
                }
            }
        } catch (e) {
            // The request never got an answer from the API (network, 5xx). Say so, rather than leaving the
            // previous list on screen with no explanation - an unexplained empty page is what hid a dead Seq
            // read key in prd for as long as it did.
            const detail = e instanceof Error ? e.message : String(e);
            this.status = {
                seqServer: null,
                apiKeyConfigured: false,
                apiKeyUsed: false,
                apiKeyLength: 0,
                succeeded: false,
                httpStatusCode: null,
                message: `The log query failed before Seq answered: ${detail}`,
            };
            if (!afterId) {
                this.events = [];
            }
        } finally {
            this.loading = false;
        }
    }

    @action
    public async loadMoreAsync() {
        if (this.events.length === 0) return;
        const lastEvent = this.events[this.events.length - 1];
        await this.loadEventsAsync(lastEvent.id);
    }

    private getFromUtc(): string | null {
        const now = new Date();
        switch (this.timeRange) {
            case '5m': return new Date(now.getTime() - 5 * 60 * 1000).toISOString();
            case '15m': return new Date(now.getTime() - 15 * 60 * 1000).toISOString();
            case '1h': return new Date(now.getTime() - 60 * 60 * 1000).toISOString();
            case '6h': return new Date(now.getTime() - 6 * 60 * 60 * 1000).toISOString();
            case '24h': return new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
            default: return null;
        }
    }

    private readonly httpClient: HttpClient;
}
